import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { HomeSection } from '@/hooks/useHomeSections';
import { AbaskHeroSection } from '@/components/hero/AbaskHeroSection';
import { CinematicIntro } from '@/components/hero/CinematicIntro';
import { featureFlags } from '@/config/featureFlags';
import { SectionFrame } from '@/components/layout/SectionFrame';
import { useAboutSectorsData } from '@/hooks/useAboutSectorsData';
import { useAnimatedCounter } from '@/utils/useAnimatedCounter';
import { Building2 } from 'lucide-react';
import { InvestmentPhilosophy } from "@/components/home/sections/InvestmentPhilosophy";
import { VisionMission } from "@/components/home/sections/VisionMission";
import { AboutSectorsCarousel } from "@/components/home/sections/AboutSectorsCarousel";

interface DynamicHomeSectionProps {
  section: HomeSection;
  backgroundImage?: string;
  videoUrl?: string;
  fallbackImage?: string;
  sectionIndex?: number;
  overrideTheme?: 'light' | 'dark';
}

const DynamicHomeSection: React.FC<DynamicHomeSectionProps> = ({ 
  section, 
  backgroundImage, 
  videoUrl,
  fallbackImage,
  sectionIndex = 0,
  overrideTheme,
}) => {
  const { language } = useLanguage();
  
  const title = language === 'ar' ? section.title_ar : section.title_en;
  const subtitle = language === 'ar' ? section.subtitle_ar : section.subtitle_en;
  const content = language === 'ar' ? section.content_ar : section.content_en;
  const buttonText = language === 'ar' ? section.button_text_ar : section.button_text_en;

  // Deterministic alternation by index: 0 dark (hero), 1 light, 2 dark, ...
  const dataTheme: 'light' | 'dark' = overrideTheme ?? (sectionIndex % 2 === 0 ? 'dark' : 'light');

  // Fallback backgrounds by known keys or generic image
  const fallbackByKey: Record<string, string> = {
    hero: '/op-uploads/102604dd-a9aa-47f8-a7cc-9bd856994a25.png',
    about: '/op-uploads/010ee35f-f81c-4de9-98d3-0804fd4ee803.png',
    vision: '/op-uploads/6c14e3c1-49c9-4615-ba5b-e7d6012d332e.png',
    contact: '/op-uploads/2025/09/image/1758042473782-67b2171e-bb6b-4065-aa00-8344a6783337.webp',
    'contact_cta': '/op-uploads/2025/09/image/1758042473782-67b2171e-bb6b-4065-aa00-8344a6783337.webp',
    'contact-cta': '/op-uploads/2025/09/image/1758042473782-67b2171e-bb6b-4065-aa00-8344a6783337.webp',
  };
  const genericFallback = '/op-uploads/default-internal-bg.png';
  const resolvedBg = backgroundImage || fallbackImage || fallbackByKey[section.section_key] || genericFallback;

  // Different layouts based on section_key
  const renderHeroSection = () => (
    <SectionFrame
      noScale
      noOffset
      className="fullpage-section pg-section"
      data-theme={dataTheme}
      data-section-index={sectionIndex}
      overlayClassName="bg-black/50 bg-gradient-to-b from-black/50 via-black/55 to-black/60"
      backgroundSlot={
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="/op-uploads/DSC_5226.webp"
          className="w-full h-full object-cover"
          preload="metadata"
        >
          <source src="/op-uploads/videos/2025/10/video/1761066148405-6a7492e4-81e5-4c3f-a66b-047eb2e19459.mp4" type="video/mp4" />
        </video>
      }
    >
      {featureFlags.FEATURE_CINEMATIC_HERO ? (
        <CinematicIntro section={section} className="h-full" />
      ) : (
        <AbaskHeroSection section={section} className="h-full" />
      )}
    </SectionFrame>
  );

  const renderAboutSection = () => (
    <SectionFrame 
      className="fullpage-section pg-section relative overflow-hidden [--section-offset-ratio:0.6] sm:[--section-offset-ratio:1]"
      data-theme={dataTheme}
      data-section-index={sectionIndex}
      style={{
        backgroundImage: `url('${resolvedBg}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      overlayClassName={
        dataTheme === 'dark'
          ? 'bg-gradient-to-b from-[#1f336366] via-[#172b57e0] to-[#081128f2] backdrop-blur-sm'
          : 'bg-white/70'
      }
    >
      
      {/* Content */}
      <div className="relative z-20 h-full flex">
        <div className="container mx-auto px-6 py-10 md:py-16 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center justify-items-center w-full">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6 max-w-2xl w-full mx-auto lg:mx-0"
          >
            <h2 className={`text-4xl lg:text-5xl font-bold mb-6 ${
              dataTheme === 'dark' ? 'text-white' : 'text-primary'
            } ${language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'}`}>
              {title}
            </h2>
            <p className={`text-lg leading-relaxed ${
              dataTheme === 'dark' ? 'text-white/85' : 'text-muted-foreground'
            } ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
              {content}
            </p>
            {buttonText && (
              <button 
                className={dataTheme === 'dark' ? 'btn-primary' : 'btn-secondary'}
                onClick={() => {
                  if (section.button_link) {
                    if (section.button_link.startsWith('#')) {
                      document.querySelector(section.button_link)?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.open(section.button_link, '_blank');
                    }
                  }
                }}
              >
                {buttonText}
              </button>
            )}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <img
              src="/op-uploads/a607d0de-4b0e-4ec2-bcf2-53e505121fe4.png"
              alt={title || "Section image"}
              className="rounded-lg shadow-card w-full h-96 object-cover"
            />
          </motion.div>
          </div>
        </div>
      </div>
    </SectionFrame>
  );

  const renderVisionSection = () => (
    <SectionFrame 
      center
      className="fullpage-section pg-section relative overflow-hidden"
      data-theme={dataTheme}
      data-section-index={sectionIndex}
      style={{
        backgroundImage: `url('${resolvedBg}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      overlayClassName={dataTheme === 'dark' ? 'bg-black/45' : 'bg-white/70'}
    >
      
      {/* Content */}
  <div className="relative z-20 container mx-auto px-6 py-16 text-center h-full flex items-center max-w-7xl">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8 md:mb-16"
          >
            <h2 className={`text-3xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-8 ${
              dataTheme === 'dark' ? 'text-white' : 'text-primary'
            } ${language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'}`}>
              {title}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center md:text-right px-2"
            >
              <h3 className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 ${
                dataTheme === 'dark' ? 'text-white' : 'text-primary'
              } ${language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'}`}>
                {language === 'ar' ? 'رؤيتنا' : 'Our Vision'}
              </h3>
              <p className={`text-lg md:text-xl leading-relaxed ${
                dataTheme === 'dark' ? 'text-white/85' : 'text-muted-foreground'
              } ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
                {subtitle}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-center md:text-left px-2"
            >
              <h3 className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 ${
                dataTheme === 'dark' ? 'text-white' : 'text-primary'
              } ${language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'}`}>
                {language === 'ar' ? 'رسالتنا' : 'Our Mission'}
              </h3>
              <p className={`text-lg md:text-xl leading-relaxed ${
                dataTheme === 'dark' ? 'text-white/85' : 'text-muted-foreground'
              } ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
                {content}
              </p>
            </motion.div>
          </div>

          {buttonText && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-8 md:mt-16"
            >
              <button 
                className={`${dataTheme === 'dark' ? 'btn-primary' : 'btn-secondary'} text-base md:text-lg px-6 md:px-8 py-3 md:py-4`}
                onClick={() => {
                  if (section.button_link) {
                    if (section.button_link.startsWith('#')) {
                      document.querySelector(section.button_link)?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.open(section.button_link, '_blank');
                    }
                  }
                }}
              >
                {buttonText}
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </SectionFrame>
  );

  // Default content section
  const renderContentSection = () => (
    <SectionFrame 
      center
      className="fullpage-section pg-section relative overflow-hidden"
      data-theme={dataTheme}
      data-section-index={sectionIndex}
      style={{
        backgroundImage: `url('${resolvedBg}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      overlayClassName={dataTheme === 'dark' ? 'bg-black/45' : 'bg-white/60'}
    >
  <div className="relative z-20 container mx-auto px-6 py-16 h-full flex items-center max-w-7xl">
        <div className="max-w-4xl mx-auto text-center">
          {title && (
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className={`text-4xl lg:text-6xl font-bold mb-6 ${
                dataTheme === 'dark' ? 'text-white' : 'text-primary'
              } ${language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'}`}
            >
              {title}
            </motion.h2>
          )}
          
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={`text-xl mb-8 ${
                dataTheme === 'dark' ? 'text-white/90' : 'text-muted-foreground'
              } ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}
            >
              {subtitle}
            </motion.p>
          )}
          
          {content && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className={`text-lg leading-relaxed mb-8 ${
                dataTheme === 'dark' ? 'text-white/80' : 'text-muted-foreground'
              } ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
          
          {buttonText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <button 
                className={`${dataTheme === 'dark' ? 'btn-primary' : 'btn-secondary'} text-lg px-8 py-4`}
                onClick={() => {
                  if (section.button_link) {
                    if (section.button_link.startsWith('#')) {
                      document.querySelector(section.button_link)?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.open(section.button_link, '_blank');
                    }
                  }
                }}
              >
                {buttonText}
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </SectionFrame>
  );

  // Enhanced About + Sectors section with 3D Carousel
  const renderAboutSectorsSection = () => {
    return <AboutSectorsCarousel 
      backgroundImage={resolvedBg}
      dataTheme="dark" // Force dark theme as requested
      sectionIndex={sectionIndex}
    />;
  };

  // Render based on section_key
  switch (section.section_key) {
    case 'hero':
      return renderHeroSection();
    case 'about':
    case 'about-sectors':
      return renderAboutSectorsSection();
    case 'vision':
      return <VisionMission sectionIndex={sectionIndex} backgroundImage={resolvedBg} />;
    case 'investment-philosophy':
    case 'philosophy':
      return (
        <InvestmentPhilosophy sectionIndex={sectionIndex} />
      );
    default:
      return renderContentSection();
  }
};

export default DynamicHomeSection;